import api from "../utils/api";

export interface Cycle {
  _id: string;
  userId: string;
  professionID?: string;  // Profession associated with this cycle
  cycleid?: number;  // Auto-incrementing numeric ID used for class associations
  // Support both legacy string state and new numeric stateID for compatibility
  state?: string;
  stateID?: number;
  stateid?: number;  // Backend uses lowercase stateid
  cyclename: string;
  cyclestart: Date | string;
  cycleend: Date | string;
  creditsRequired: number;
  note?: string;        // Optional user memo for this cycle
  isCustom?: boolean;
  customProfessionName?: string;
  customStateName?: string;
}

export type CycleInput = {
  cyclename: string;
  state: string;  // State name
  stateid: number;  // Numeric state ID (lowercase to match backend)
  professionID?: string;  // Profession ID for this cycle
  cyclestart: Date | string;
  cycleend: Date | string;
  creditsRequired: number;
};

export interface CycleFilterParams {
  search?: string;
  stateID?: number;
  status?: "active" | "archived";
  sortBy?: "cyclename" | "cyclestart" | "cycleend" | "state";
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface CycleFilterResponse {
  cycles: Cycle[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    totalCycles: number;
    activeCycles: number;
    archivedCycles: number;
    statesManaged: number;
  };
}

export const getFilteredCycles = async (params: CycleFilterParams): Promise<CycleFilterResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append("search", params.search);
    if (params.stateID) queryParams.append("stateID", params.stateID.toString());
    if (params.status) queryParams.append("status", params.status);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    
    const res = await api.get(`/api/cycles/filter?${queryParams.toString()}`);
    return res.data;
  } catch (error) {
    console.error("API: Error fetching filtered cycles:", error);
    throw error;
  }
};

export const getCyclesForUserId = async (userId: string): Promise<Cycle[]> => {
  const res = await api.get(`/api/cycles/user/${userId}`);
  return res.data || [];
};

// Cycles soft-deleted by a plan downgrade (isDeleted + deletedReason='plan_downgrade')
// for the logged-in user — visible, read-only, restorable only by upgrading.
export const getLockedCycles = async (): Promise<Cycle[]> => {
  const res = await api.get(`/api/cycles/locked`);
  return res.data || [];
};

export const getCyclesForUser = async (): Promise<Cycle[]> => {
  try {
    console.log('API: Fetching cycles from /api/cycles');
    const res = await api.get(`/api/cycles`);
    console.log('API: Cycles response:', res.data);
    console.log('API: Cycles count:', res.data?.length || 0);
    return res.data || [];
  } catch (error) {
    console.error('API: Error fetching cycles:', error);
    throw error;
  }
};

export const addCycle = async (cycleData: CycleInput): Promise<Cycle> => {
  try {
    console.log('API: Adding cycle with data:', cycleData);
    const res = await api.post('/api/cycles', cycleData);
    console.log('API: Add cycle response:', res.data);
    return res.data;
  } catch (error) {
    console.error('API: Error adding cycle:', error);
    throw error;
  }
};

export interface BulkCreateResult {
  created: Cycle[];
  skipped: { professionID: string; stateID: number; licenseNumber: string; reason: string }[];
  message: string;
}

export const bulkCreateCyclesFromLicenses = async (): Promise<BulkCreateResult> => {
  const res = await api.post('/api/cycles/bulk-from-licenses');
  return res.data;
};

export const addCustomCycle = async (customCycleData: {
  cyclename: string;
  cyclestart: string;
  cycleend: string;
  customTotalCredits: number;
  customStateName?: string;
  customProfessionName?: string;
  stateid?: number;
  professionID?: string;
  note?: string;
}): Promise<Cycle> => {
  try {
    console.log('API: Adding custom cycle with data:', customCycleData);
    const res = await api.post('/api/cycles/custom', customCycleData);
    console.log('API: Add custom cycle response:', res.data);
    return res.data;
  } catch (error) {
    console.error('API: Error adding custom cycle:', error);
    throw error;
  }
};

export const updateCycle = async (cycleId: string, cycleData: Partial<CycleInput>): Promise<Cycle> => {
  const res = await api.put(`/api/cycles/${cycleId}`, cycleData); // userId is already in the token, not needed in body
  return res.data;
};

export const deleteCycle = async (cycleId: string): Promise<void> => {
  try {
    console.log('API: Deleting cycle with ID:', cycleId);
    const res = await api.delete(`/api/cycles/${cycleId}`);
    console.log('API: Delete cycle response:', res.data);
  } catch (error: any) {
    console.error('API: Error deleting cycle:', error);
    console.error('API: Error response:', error?.response);
    console.error('API: Error status:', error?.response?.status);
    console.error('API: Error message:', error?.response?.data);
    throw error;
  }
};

// Attach a class to a cycle using numeric cycleid; body can include classId (mongo) or classid (number)
// Backend returns { attached: boolean, already: boolean, message, cycle, class }
// Treat both attached=true and already=true as success
export const attachClassToCycle = async (
  cycleid: number,
  payload: { classId?: string; classid?: number }
): Promise<any> => {
  const res = await api.post(`/api/cycles/${cycleid}/classes`, payload);
  const data = res.data;
  // If attached or already associated, treat as success
  if (data.attached || data.already) {
    return data;
  }
  // Otherwise throw with server message
  throw new Error(data?.message || 'Failed to attach class to cycle');
};

// Optionally remove a class from a cycle using numeric ids (if classid numeric is known)
export const removeClassFromCycle = async (cycleid: number, classid: number): Promise<any> => {
  const res = await api.delete(`/api/cycles/${cycleid}/classes/${classid}`);
  return res.data;
};